import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, Loader2, DatabaseBackup, Download, Upload, GitMerge, Replace, CheckCircle2, FileSpreadsheet } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { tagThemeClass } from './tagTheme';

const CHUNK = 1024 * 1024;
const MODES = [
  { id: 'merge', icon: GitMerge, title: 'Merge with current data', text: 'Matching tags (same ID, or same company + jewellery no.) are updated from the backup and new ones are added. Nothing is removed.' },
  { id: 'replace', icon: Replace, title: 'Replace current data', text: 'The tag portal becomes exactly the backup. Manual tags not in the backup are deleted; main-portal pieces not in it are hidden (restorable).' },
];
const LABELS = { tag_lists: 'Saved lists', tag_rate_cards: 'Rate cards', tag_rules: 'Tag rules', tag_view_passwords: 'QR passwords', tag_settings: 'Settings', tag_display_names: 'Display names', tag_charge_names: 'Charge names', tag_company_presets: 'Label presets', tag_sales: 'Sold lists', tag_sales_returns: 'Sales returns', tag_approvals: 'Approvals', goldsmiths: 'Goldsmiths added', customers: 'Customers added' };

async function downloadBackup() {
  const res = await notesApi.get('/notes/tag-workspace/backup/export', { responseType: 'blob', timeout: 0 });
  const name = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'metriqs-backup.xlsx';
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

async function readBlobError(e) {
  if (e?.response?.data instanceof Blob) {
    try { const j = JSON.parse(await e.response.data.text()); return j.detail || ''; } catch { return ''; }
  }
  return '';
}

function RestoreResult({ r }) {
  const j = r.jewellery || {};
  return (
    <div className="rounded-xl border border-emerald-600/30 bg-emerald-600/5 p-3.5 text-sm" data-testid="tagws-backup-result">
      <div className="mb-2 flex items-center gap-1.5 font-semibold text-foreground"><CheckCircle2 size={15} className="text-emerald-600" /> Restore complete ({r.mode})</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>Tags added: <b className="text-foreground">{j.added || 0}</b></span>
        <span>Tags updated: <b className="text-foreground">{j.updated || 0}</b></span>
        {r.mode === 'replace' ? <><span>Manual tags removed: <b className="text-foreground">{j.removed || 0}</b></span><span>Portal pieces hidden: <b className="text-foreground">{j.hidden || 0}</b></span></> : null}
        {Object.entries(LABELS).map(([k, l]) => (r[k] != null ? <span key={k}>{l}: <b className="text-foreground">{r[k]}</b></span> : null))}
      </div>
    </div>
  );
}

// Ctrl + B B B — super admin Excel backup (tags + images + lists + sales + returns + rates + rules) and restore.
export function TagBackupDialog({ onClose, onRestored }) {
  const [busy, setBusy] = useState('');
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('merge');
  const [sure, setSure] = useState(false);
  const [pct, setPct] = useState(0);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [busy, onClose]);

  const backup = async () => {
    setBusy('backup');
    try { await downloadBackup(); toast.success('Backup downloaded.'); } catch (e) { toast.error((await readBlobError(e)) || extractError(e, 'Could not create the backup.')); } finally { setBusy(''); }
  };
  const restore = async () => {
    if (!file) { toast.error('Choose a backup file first.'); return; }
    setBusy('restore'); setResult(null); setPct(0);
    try {
      const uid = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      const total = Math.max(1, Math.ceil(file.size / CHUNK));
      for (let i = 0; i < total; i += 1) {
        const fd = new FormData();
        fd.append('upload_id', uid); fd.append('index', String(i)); fd.append('chunk', file.slice(i * CHUNK, (i + 1) * CHUNK), 'chunk');
        await notesApi.post('/notes/tag-workspace/backup/upload', fd);
        setPct(Math.round(((i + 1) / total) * 90));
      }
      const { data } = await notesApi.post('/notes/tag-workspace/backup/restore', { upload_id: uid, mode }, { timeout: 0 });
      setPct(100); setResult(data); setFile(null); setSure(false);
      if (inputRef.current) inputRef.current.value = '';
      toast.success('Backup restored.');
      onRestored();
    } catch (e) { toast.error(extractError(e, 'Could not restore the backup.')); } finally { setBusy(''); }
  };
  const canRestore = file && !busy && (mode === 'merge' || sure);
  return (
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[220] flex items-center justify-center p-3`} data-testid="tagws-backup-dialog">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" onClick={() => !busy && onClose()} />
      <div className="relative z-10 max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 pb-4 pt-5">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><DatabaseBackup size={20} /></span>
            <div>
              <h3 className="text-base font-semibold text-foreground">Backup &amp; restore</h3>
              <p className="text-xs text-muted-foreground">Super admin only · all companies</p>
            </div>
          </div>
          <button onClick={onClose} disabled={!!busy} aria-label="Close" className="rounded-lg p-1 text-muted-foreground hover:bg-secondary" data-testid="tagws-backup-close"><X size={18} /></button>
        </div>
        <div className="space-y-5 p-5">
          <section>
            <div className="mb-1 text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold uppercase tracking-[0.14em] text-muted-foreground">1 · Backup</div>
            <p className="mb-3 text-xs text-muted-foreground">One Excel file with every tag and its photos, saved lists, sold lists, sales returns, rate cards, tag rules, extra charges, goldsmiths and customers.</p>
            <button type="button" onClick={backup} disabled={!!busy} className="inline-flex h-10 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-transform duration-150 hover:-translate-y-0.5 disabled:opacity-60" data-testid="tagws-backup-download">
              {busy === 'backup' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {busy === 'backup' ? 'Preparing backup…' : 'Download backup (Excel)'}
            </button>
          </section>
          <section className="border-t border-border pt-5">
            <div className="mb-1 text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold uppercase tracking-[0.14em] text-muted-foreground">2 · Restore</div>
            <label className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-border p-3.5 transition-colors hover:bg-secondary/50" data-testid="tagws-backup-file-label">
              <FileSpreadsheet size={20} className="shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">{file ? file.name : 'Choose a backup .xlsx file'}</span>
              <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="tagws-backup-file-input" />
            </label>
            <div className="mt-3 grid gap-2">
              {MODES.map((m) => {
                const I = m.icon;
                return (
                  <button key={m.id} type="button" onClick={() => { setMode(m.id); setSure(false); }} className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${mode === m.id ? (m.id === 'replace' ? 'border-red-600/60 bg-red-600/5' : 'border-[hsl(var(--ring))] bg-[hsl(var(--ring))]/5') : 'border-border hover:bg-secondary/50'}`} data-testid={`tagws-backup-mode-${m.id}`}>
                    <I size={16} className={`mt-0.5 shrink-0 ${m.id === 'replace' ? 'text-red-600' : 'text-primary'}`} />
                    <span><span className="block text-sm font-semibold text-foreground">{m.title}</span><span className="block text-xs text-muted-foreground">{m.text}</span></span>
                  </button>
                );
              })}
            </div>
            {mode === 'replace' ? (
              <label className="mt-3 flex items-center gap-2 text-xs text-foreground" data-testid="tagws-backup-replace-confirm">
                <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} className="h-4 w-4 accent-red-600" data-testid="tagws-backup-replace-confirm-checkbox" /> I understand the current tag data will be replaced.
              </label>
            ) : null}
            {busy === 'restore' ? <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-secondary" data-testid="tagws-backup-progress"><div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${pct}%` }} /></div> : null}
            <button type="button" onClick={restore} disabled={!canRestore} className={`mt-3 inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold text-white disabled:opacity-50 ${mode === 'replace' ? 'bg-red-600 hover:bg-red-700' : 'bg-foreground hover:opacity-90'}`} data-testid="tagws-backup-restore">
              {busy === 'restore' ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {busy === 'restore' ? (pct < 90 ? `Uploading… ${pct}%` : 'Restoring…') : mode === 'replace' ? 'Restore & replace' : 'Restore & merge'}
            </button>
          </section>
          {result ? <RestoreResult r={result} /> : null}
        </div>
      </div>
    </div>
  );
}
